import React, { useState } from 'react';
import { useTranslation } from '../../../context/LanguageContext';
import SpcNew from './SpcNew';
import ShotJudgeNew from './ShotJudgeNew';
import VisionInspectionNew from './VisionInspectionNew';
import FusionInspectionNew from './FusionInspectionNew';
import ShotSyncNew from './ShotSyncNew';
import AutoLabelNew from './AutoLabelNew';
import ProductionNew from './ProductionNew';
import './InspectionPhotos.css';

/* ===== 품질·생산 탭 (서브탭 컨테이너) =====
 * 사출기 대시보드의「품질·생산」탭. 파일명·탭 내부 이름('검수사진')은 다른 화면의 이동 링크가 쓰므로 유지한다.
 *
 * 서브탭 (키 → 화면):
 *   spc        공정관리 (SPC)   ./SpcNew
 *   judge      공정검사         ./ShotJudgeNew        — 공정 AI(LSTM) 샷 판정
 *   photos     비전검사         ./VisionInspectionNew — 비전 이미지 판정·불량 유형 (예전「검수 사진」)
 *   sync       샷동기화         ./ShotSyncNew         — 공정 샷 ↔ 비전 이미지 짝짓기 (Shot-Product Sync Engine)
 *   fusion     융합검사         ./FusionInspectionNew — 공정+비전 융합 최종 판정 (Fusion Decision Engine)
 *   label      자동라벨링       ./AutoLabelNew        — 검사 결과로 붙인 자동 라벨·신뢰도 (Auto Labeling Pipeline)
 *   production 생산             ./ProductionNew
 * 키는 다른 화면에서 initialSubTab 으로 넘기므로(예: 개요 > 품질 요약 → 'judge') 바꾸지 않는다.
 *
 * 예전 검수 사진의 측면 사진 업로드(브라우저 미리보기만, 서버 저장 없음)는 오해 소지가 있어 2026-10-28 제거. */

const SUB_TABS = ['spc', 'judge', 'photos', 'sync', 'fusion', 'label', 'production'];
const LABEL_KEYS = {
  spc: 'subTabSpc',
  judge: 'subTabJudge',
  photos: 'subTabPhotos',
  sync: 'subTabSync',
  fusion: 'subTabFusion',
  label: 'subTabLabel',
  production: 'subTabProduction'
};

function InspectionPhotos({ equipmentId, machine, initialSubTab = 'photos' }) {
  const { t } = useTranslation();
  const tr = (key, params) => t(`pages.monitoring.machine.inspectionPhotos.${key}`, params);

  const [activeSub, setActiveSub] = useState(SUB_TABS.includes(initialSubTab) ? initialSubTab : 'photos');

  return (
    <div className="ip-root">
      <div className="ip-subtabs" style={{ gridTemplateColumns: `repeat(${SUB_TABS.length}, 1fr)` }}>
        {SUB_TABS.map((key) => (
          <button
            key={key}
            type="button"
            className={`ip-subtab ${activeSub === key ? 'active' : ''}`}
            onClick={() => setActiveSub(key)}
          >
            {tr(LABEL_KEYS[key])}
          </button>
        ))}
      </div>

      {activeSub === 'spc' && <SpcNew equipmentId={equipmentId} />}

      {/* 공정검사 — 공정 AI 샷 판정. "AI 분석 자세히 보기"는 그 안에서 전체 LSTM 화면을 펼친다 */}
      {activeSub === 'judge' && <ShotJudgeNew equipmentId={equipmentId} />}

      {/* 비전검사 — 비전 이미지 판정·불량 유형별 현황 */}
      {activeSub === 'photos' && <VisionInspectionNew equipmentId={equipmentId} />}

      {/* 샷동기화 — 공정 샷과 비전 이미지 중 같은 제품끼리 짝지은 결과 (융합검사의 입력) */}
      {activeSub === 'sync' && <ShotSyncNew equipmentId={equipmentId} />}

      {/* 융합검사 — 공정검사 + 비전검사를 Fusion Decision Engine 가중치로 합친 최종 판정 */}
      {activeSub === 'fusion' && <FusionInspectionNew equipmentId={equipmentId} />}

      {/* 자동라벨링 — 검사 결과로 붙인 라벨과 신뢰도. 신뢰도 미달은 AI 분석 > 라벨 검토에서 작업자가 확정 */}
      {activeSub === 'label' && <AutoLabelNew equipmentId={equipmentId} />}

      {/* 생산 — machine(설비 카드)은 가동률 표시에 쓴다 */}
      {activeSub === 'production' && <ProductionNew equipmentId={equipmentId} machine={machine} />}
    </div>
  );
}

export default InspectionPhotos;
